<template>
  <dashboard-collapsible-card
    title="Utilisateurs actifs"
    :collapsed="collapsed"
    card-class="dashboard-overview-card dashboard-overview-card--active-users dashboard-tracking-overview-card dashboard-tracking-overview-card--active-users h-full flex flex-col"
    collapsed-icon="fas fa-user-check text-primary-600"
    header-class="dashboard-card-header"
    content-class="dashboard-tracking-card__content"
    @toggle="$emit('toggle')"
  >
    <template #header-actions>
      <div class="flex flex-wrap gap-2">
        <dashboard-metric-chip label="Connexions">
          <template #value>
            <span class="flex items-center gap-1 text-base font-semibold text-primary-600">
              <span v-if="loadingGraphs"><i class="fas fa-spinner fa-spin"></i></span>
              <span v-else>{{ formatNumber(activeLogins) }}</span>
            </span>
          </template>
        </dashboard-metric-chip>
        <dashboard-metric-chip label="Connexions uniques">
          <template #value>
            <span class="flex items-center gap-1 text-base font-semibold text-primary-600">
              <span v-if="loadingGraphs"><i class="fas fa-spinner fa-spin"></i></span>
              <span v-else>{{ formatNumber(activeUsers) }}</span>
            </span>
          </template>
        </dashboard-metric-chip>
      </div>
    </template>

    <template #collapsed-description>
      <p class="text-xs text-gray-600">
        Connexions: <strong class="text-gray-900">{{ formatNumber(activeLogins) }}</strong>
        · Unique: <strong class="text-gray-900">{{ formatNumber(activeUsers) }}</strong>
      </p>
    </template>

    <div class="dashboard-tracking-card-body">
      <div class="dashboard-tracking-chart-area">
        <div class="dashboard-tracking-chart-panel">
          <dashboard-list-state
            :loading="loadingGraphs"
            :has-data="hasActiveUsersSeries"
            class="h-full"
          >
            <template #default>
              <chart-connection-trend
                class="h-full w-full"
                :chart-data="activeUsersChartData"
                :show-legend="false"
              />
            </template>
            <template #loading>
              <div class="space-y-4">
                <div class="dashboard-skeleton h-full min-h-[9rem] rounded-xl"></div>
                <div class="dashboard-skeleton h-3 w-2/3 rounded-full"></div>
              </div>
            </template>
            <template #empty>
              <div class="dashboard-tracking-empty-state">
                Aucun utilisateur actif
              </div>
            </template>
          </dashboard-list-state>
        </div>
      </div>

      <article class="dashboard-tracking-ring-card">
        <div v-if="loadingGraphs" class="flex w-full flex-col items-center justify-center gap-4">
          <div class="dashboard-skeleton h-[7rem] w-[7rem] rounded-full"></div>
          <div class="dashboard-skeleton h-3 w-3/4 rounded-full"></div>
          <div class="dashboard-skeleton h-3 w-2/3 rounded-full"></div>
        </div>
        <template v-else>
          <div class="dashboard-tracking-ring">
            <svg viewBox="0 0 120 120" aria-hidden="true">
              <circle class="dashboard-tracking-ring-bg" cx="60" cy="60" r="48" />
              <circle
                class="dashboard-tracking-ring-progress dashboard-tracking-ring-progress--active"
                cx="60"
                cy="60"
                r="48"
                :stroke-dasharray="ringCircumference"
                :stroke-dashoffset="activeUsersRingStrokeOffset"
              />
            </svg>
            <span>{{ formatPercent(activeUsersRingValue) }}</span>
          </div>
          <p>{{ activeUsersRingLabel }}</p>
          <div
            v-if="activeUsersRingOptions.length > 1"
            class="dashboard-tracking-segmented-control dashboard-tracking-segmented-control--compact dashboard-tracking-segmented-control--active"
            aria-label="Formule utilisateurs actifs"
          >
            <button
              v-for="option in activeUsersRingOptions"
              :key="option.value"
              type="button"
              :class="{ 'is-active': activeUsersRingMode === option.value }"
              @click="activeUsersRingMode = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </template>
      </article>
    </div>
  </dashboard-collapsible-card>
</template>

<script>
import DashboardCollapsibleCard from '@/components/dashboard/DashboardCollapsibleCard.vue'
import DashboardListState from '@/components/dashboard/DashboardListState.vue'
import DashboardMetricChip from '@/components/dashboard/DashboardMetricChip.vue'
import ChartConnectionTrend from '@/components/charts/ChartConnectionTrend.vue'

export default {
  name: 'DashboardLoginsCard',
  components: {
    DashboardCollapsibleCard,
    DashboardListState,
    DashboardMetricChip,
    ChartConnectionTrend
  },
  props: {
    collapsed: {
      type: Boolean,
      required: true
    },
    totals: {
      type: Object,
      default: () => ({})
    },
    dailySeries: {
      type: Array,
      default: () => []
    },
    loadingGraphs: {
      type: Boolean,
      default: false
    },
    hasDomainFilter: {
      type: Boolean,
      default: true
    },
    formatNumber: {
      type: Function,
      required: true
    }
  },
  data () {
    return {
      activeUsersRingMode: 'downloads'
    }
  },
  emits: ['toggle'],
  computed: {
    activeLogins () {
      return this.safeNumber(this.totals?.activeLogins)
    },
    activeUsers () {
      return this.safeNumber(this.totals?.activeUsers)
    },
    normalizedDailySeries () {
      const rows = Array.isArray(this.dailySeries) ? this.dailySeries : []
      return rows.map(row => ({
        date: row?.date || '',
        activeLogins: this.safeNumber(row?.activeLogins),
        activeUsers: this.safeNumber(row?.activeUsers),
        activeUsersRateDownloads: this.safeNumber(row?.activeUsersRateDownloads),
        activeUsersRateAverage: this.safeNumber(row?.activeUsersRateAverage)
      }))
    },
    hasActiveUsersSeries () {
      return this.normalizedDailySeries.some(row => row.activeUsers > 0 || row.activeUsersRateDownloads > 0 || row.activeUsersRateAverage > 0)
    },
    activeUsersRingOptions () {
      const options = [
        { value: 'downloads', label: 'DL' },
        { value: 'average', label: 'Moyenne' }
      ]

      return this.hasDomainFilter
        ? options.filter(option => option.value !== 'average')
        : options
    },
    activeUsersChartConfig () {
      return {
        label: 'UA uniques',
        key: 'activeUsers',
        color: '#2563eb'
      }
    },
    activeUsersChartData () {
      return this.buildLineChartData(this.activeUsersChartConfig)
    },
    ringCircumference () {
      return 2 * Math.PI * 48
    },
    activeUsersRingValue () {
      return this.activeUsersRingMode === 'average'
        ? this.safeNumber(this.totals?.activeUsersRateAverage)
        : this.safeNumber(this.totals?.activeUsersRateDownloads)
    },
    activeUsersRingLabel () {
      return this.activeUsersRingMode === 'average'
        ? 'Moyenne des clients actifs'
        : 'UA uniques / DL total'
    },
    activeUsersRingStrokeOffset () {
      return this.buildRingStrokeOffset(this.activeUsersRingValue)
    }
  },
  watch: {
    activeUsersRingOptions: {
      handler () {
        this.ensureModeIsAvailable('activeUsersRingMode', this.activeUsersRingOptions)
      },
      immediate: true
    }
  },
  methods: {
    safeNumber (value) {
      const numeric = Number(value)
      return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0
    },
    ensureModeIsAvailable (modeKey, options) {
      const values = options.map(option => option.value)
      if (!values.includes(this[modeKey]) && values.length > 0) {
        this[modeKey] = values[0]
      }
    },
    buildLineChartData (config) {
      const color = config.color
      return {
        labels: this.normalizedDailySeries.map(item => this.formatDateLabel(item.date)),
        datasets: [
          {
            label: config.label,
            data: this.normalizedDailySeries.map(item => this.safeNumber(item[config.key])),
            borderColor: color,
            backgroundColor: this.hexToRgba(color, 0.12),
            borderWidth: this.normalizedDailySeries.length > 31 ? 0.75 : 1,
            pointBackgroundColor: color,
            pointRadius: this.normalizedDailySeries.length > 31 ? 0 : (this.normalizedDailySeries.length > 14 ? 1 : 1.5),
            pointHoverRadius: 2,
            pointHitRadius: 8,
            fill: false
          }
        ]
      }
    },
    buildRingStrokeOffset (value) {
      const progress = Math.min(100, Math.max(0, Number(value || 0)))
      return this.ringCircumference * (1 - (progress / 100))
    },
    formatDateLabel (value) {
      if (!value || typeof value !== 'string' || value.length < 10) {
        return value || ''
      }

      return `${value.slice(8, 10)}/${value.slice(5, 7)}`
    },
    formatPercent (value) {
      return `${new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(value || 0))} %`
    },
    hexToRgba (hex, opacity) {
      const value = String(hex || '').replace('#', '')
      if (value.length !== 6) {
        return `rgba(37, 99, 235, ${opacity})`
      }

      const r = parseInt(value.slice(0, 2), 16)
      const g = parseInt(value.slice(2, 4), 16)
      const b = parseInt(value.slice(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }
  }
}
</script>
